import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import StandardLessonsPage from './pages/StandardLessonsPage';
import IntensiveLessonsPage from './pages/IntensiveLessonsPage';
import AdditionalLessonsPage from './pages/AdditionalLessonsPage';
import ContactPage from './pages/ContactPage';
import FAQPage from './pages/FAQPage';
import TipsPage from './pages/TipsPage';
import CancellationPage from './pages/CancellationPage';
import PrivacyCookiePage from './pages/PrivacyCookiePage';
import TermsPage from './pages/TermsPage';
import TotalDrivePage from './pages/TotalDrivePage';
import ScrollToTop from './components/ScrollToTop';
import FacebookPixel from './components/FacebookPixel';
import GoogleAnalytics from './components/GoogleAnalytics';
// Blog Pages
import BlogsPage from './pages/BlogsPage';
import Blog1PassFirstTime from './pages/blogs/Blog1PassFirstTime';
import Blog2AutomaticLessons from './pages/blogs/Blog2AutomaticLessons';
import Blog3IntensiveCourses from './pages/blogs/Blog3IntensiveCourses';
import Blog4NervousDrivers from './pages/blogs/Blog4NervousDrivers';
// Function to check if we need to redirect from www to non-www
const useWwwRedirect = () => {
  useEffect(() => {
    // Check if we're on www subdomain and redirect to non-www
    if (window.location.hostname.startsWith('www.')) {
      const newUrl = window.location.protocol + '//' + window.location.hostname.replace('www.', '') + window.location.pathname + window.location.search;
      window.location.replace(newUrl);
    }
  }, []);
};
export function App() {
  // Use the www redirect hook
  useWwwRedirect();
  return <HelmetProvider data-id="element-0" data-mp="sunf2d84aoj8">
      <Helmet data-id="element-1" data-mp="stuexv3nyxa2">
        <meta name="description" content="Affordable automatic driving lessons, intensive courses, and refresher training in Northampton with patient, DVSA-approved instructors. Book today." data-id="element-2" data-mp="stkj3qvxtvwg" />
        <title data-id="element-3" data-mp="3ucjbrrlok7so">
          Automatic Driving Lessons Northampton | Northampton Driving School
        </title>
        <meta name="keywords" content="automatic driving lessons Northampton, driving instructor Northampton, intensive driving courses, refresher driving lessons, DVSA approved instructors, nervous driver support" data-id="element-4" data-mp="d2l9h7r2ob2y" />
        <script type="application/ld+json" data-id="element-5" data-mp="3bmtgz4hx3m0r">
          {`
            {
              "@context": "https://schema.org",
              "@type": "DrivingSchool",
              "name": "Northampton Driving School",
              "description": "Professional automatic driving lessons in Northampton with DVSA-approved instructors. Offering standard lessons, intensive courses, and specialized training for nervous drivers.",
              "url": "https://www.northamptondrivingschool.co.uk",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Northampton",
                "postalCode": "NN1 5SQ",
                "addressCountry": "UK"
              },
              "telephone": "07481 300 600",
              "email": "info@northamptondrivingschool.co.uk",
              "openingHours": "Mo-Th 09:15-19:00, Sa-Su 09:15-19:00",
              "serviceType": "Automatic Driving Lessons, Intensive Driving Courses, Refresher Lessons, Motorway Lessons",
              "areaServed": "Northampton"
            }
          `}
        </script>
        <meta property="og:title" content="Automatic Driving Lessons Northampton | Northampton Driving School" data-id="element-6" data-mp="1i3ujeg349tln" />
        <meta property="og:description" content="Learn to drive with patient, DVSA-approved instructors. Automatic lessons, intensive courses, and refresher training in Northampton." data-id="element-7" data-mp="1m3v24cx90qyi" />
        <meta property="og:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/uQmtuFR4FdAFx95dEcdiJJ/Favicon.png" data-id="element-8" data-mp="arc0qqtf0boo" />
        <meta property="og:url" content="https://northamptondrivingschool.co.uk" data-id="element-9" data-mp="m65k6vdaka91" />
        <meta property="og:type" content="website" data-id="element-10" data-mp="h8du86w7iy7h" />
        <link rel="canonical" href="https://northamptondrivingschool.co.uk" data-id="element-11" data-mp="h4znkmgig2u8" />
        <script type="application/ld+json" data-id="element-12" data-mp="ca2hnoej29lk">
          {`
            {
              "@context": "https://schema.org",
              "@type": "DrivingSchool",
              "name": "Northampton Driving School",
              "url": "https://northamptondrivingschool.co.uk",
              "logo": "https://uploadthingy.s3.us-west-1.amazonaws.com/uQmtuFR4FdAFx95dEcdiJJ/Favicon.png",
              "description": "Automatic driving lessons in Northampton. Learn to drive confidently with qualified instructors.",
              "telephone": "+447481300600",
              "priceRange": "£35-£90",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Northampton",
                "addressLocality": "Northampton",
                "addressRegion": "Northamptonshire",
                "postalCode": "NN1",
                "addressCountry": "GB"
              },
              "openingHours": "Mo-Su 08:00-20:00",
              "sameAs": [
                "https://www.facebook.com/NorthamptonDrivingSchool",
                "https://www.instagram.com/northamptondrivingschool"
              ]
            }
          `}
        </script>
      </Helmet>
      <Router data-id="element-13" data-mp="2js6etov77kna">
        <ScrollToTop data-id="element-14" data-mp="2jw3x689yc864" />
        <FacebookPixel data-id="element-15" data-mp="2nvums8nqsi92" />
        <GoogleAnalytics data-id="element-16" data-mp="2nvcq5ax6420m" />
        <Layout data-id="element-17" data-mp="2nw4djygm2tft">
          <Routes data-id="element-18" data-mp="2nti4tvhzi7w0">
            <Route path="/" element={<HomePage data-id="element-20" data-mp="2suxs7u7mwqhk" />} data-id="element-19" data-mp="2supcluk4n72o" />
            <Route path="/about" element={<AboutPage data-id="element-22" data-mp="2ssbmm04b0l1z" />} data-id="element-21" data-mp="2su5vuh67qoq8" />
            <Route path="/standard-lessons" element={<StandardLessonsPage data-id="element-24" data-mp="tj4rbmhq70zk" />} data-id="element-23" data-mp="ti3gnj4mggid" />
            <Route path="/intensive-lessons" element={<IntensiveLessonsPage data-id="element-26" data-mp="ygww3h3gyhl6" />} data-id="element-25" data-mp="ygoifmziv88x" />
            <Route path="/additional-lessons" element={<AdditionalLessonsPage data-id="element-28" data-mp="21tf0d1fy7uco" />} data-id="element-27" data-mp="21r2c66jlknr6" />
            <Route path="/contact" element={<ContactPage data-id="element-30" data-mp="1wukajfjkd5j2" />} data-id="element-29" data-mp="1wtspgynleq71" />
            <Route path="/faq" element={<FAQPage data-id="element-32" data-mp="2atbjyonhe30v" />} data-id="element-31" data-mp="2as0iwwlf79dg" />
            <Route path="/tips" element={<TipsPage data-id="element-34" data-mp="2apw6trddb423" />} data-id="element-33" data-mp="2atjyekz95ge6" />
            <Route path="/total-drive" element={<TotalDrivePage data-id="element-36" data-mp="25rm8x909dpcs" />} data-id="element-35" data-mp="25snsk1vcbk64" />
            <Route path="/cancellation" element={<CancellationPage data-id="element-38" data-mp="2kpmy7k6mt2xy" />} data-id="element-37" data-mp="25viclsafz2z0" />
            <Route path="/privacy" element={<PrivacyCookiePage data-id="element-40" data-mp="2frd1v6967war" />} data-id="element-39" data-mp="2kolobj1cqmht" />
            <Route path="/terms" element={<TermsPage data-id="element-42" data-mp="2fr3bvio4lp52" />} data-id="element-41" data-mp="2fqaeh8b24bzl" />
            <Route path="/cookie-policy" element={<PrivacyCookiePage data-id="element-44" data-mp="2thowbbt8a752" />} data-id="element-43" data-mp="2tiqjufrfkm3o" />
            {/* Blog Routes */}
            <Route path="/blogs" element={<BlogsPage data-id="element-46" data-mp="2pi7qgh9w37zd" />} data-id="element-45" data-mp="2pheri2h69mrs" />
            <Route path="/blogs/pass-first-time" element={<Blog1PassFirstTime data-id="element-48" data-mp="33hz9iwf9jek8" />} data-id="element-47" data-mp="2phyalmik6h7e" />
            <Route path="/blogs/automatic-driving-lessons" element={<Blog2AutomaticLessons data-id="element-50" data-mp="2yfqezvmqs4g0" />} data-id="element-49" data-mp="33el5ljqyaxao" />
            <Route path="/blogs/intensive-driving-courses" element={<Blog3IntensiveCourses data-id="element-52" data-mp="3n2g2vxtpznxg" />} data-id="element-51" data-mp="3i3d773zmqu25" />
            <Route path="/blogs/nervous-drivers-tips" element={<Blog4NervousDrivers data-id="element-54" data-mp="384nlf04m4pgm" />} data-id="element-53" data-mp="3n1y69035b7p0" />
          </Routes>
        </Layout>
      </Router>
    </HelmetProvider>;
}